import {useEffect, useState} from 'react';
import {Menu, Search, X, CloudSun} from '@nlabs/gothamjs/icons';
import {Link, useLocation} from '@nlabs/gothamjs/router';
import {SiteLoadArticleFeed} from './SiteLoadArticleFeed';
import {SiteLoadWeather} from './SiteLoadWeather';
import {useStoreState} from '../stores/useStoreState';
import type {WeatherSnapshot} from '@chicago-sentinel/contracts/weather';
import {sectionPath, sections} from '../data/sections';

const CHICAGO_TIME_ZONE = 'America/Chicago';

export const getEditionLabel = (date = new Date()) => {
  const chicagoHour = Number(new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hourCycle: 'h23',
    timeZone: CHICAGO_TIME_ZONE
  }).format(date));

  return chicagoHour >= 15 ? 'Evening Edition' : 'Morning Edition';
};

export const getChicagoDateLabel = (date = new Date()) => new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  timeZone: CHICAGO_TIME_ZONE,
  weekday: 'long',
  year: 'numeric'
}).format(date);

export const SiteHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dateLabel, setDateLabel] = useState(() => getChicagoDateLabel());
  const [editionLabel, setEditionLabel] = useState(() => getEditionLabel());
  const {pathname} = useLocation();
  const weather = useStoreState<WeatherSnapshot | undefined>('homepage.weather', undefined);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const now = new Date();
      setDateLabel(getChicagoDateLabel(now));
      setEditionLabel(getEditionLabel(now));
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <>
      <SiteLoadArticleFeed />
      <SiteLoadWeather />
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="edition-bar">
        <div className="shell edition-bar__inner">
          <span>{dateLabel}</span>
          <span className="edition-bar__edition">{editionLabel}</span>
          <span><Link to="/archive">Archive</Link> · <Link to="/about">About the Sentinel</Link></span>
        </div>
      </div>
      <header className="site-header">
        <div className="shell masthead-row">
          <button className="icon-button mobile-only" onClick={() => setMenuOpen(true)} aria-label="Open navigation"><Menu size={22} /></button>
          <Link className="masthead" to="/" aria-label="Chicago Sentinel Times home">
            <span className="masthead__desktop" aria-hidden="true">Chicago Sentinel Times</span>
            <span className="masthead__mobile" aria-hidden="true"><span>Chicago</span><span>Sentinel Times</span></span>
          </Link>
          <div className="weather-stamp">
            <CloudSun size={23} strokeWidth={1.5} />
            <span><strong>{weather ? `${weather.currentTemperatureF}°` : '—'}</strong><small>Chicago</small></span>
          </div>
          <button className="icon-button" onClick={() => setSearchOpen((open) => !open)} aria-label="Search"><Search size={21} /></button>
        </div>
        <nav className="section-nav" aria-label="Primary navigation">
          <div className="shell section-nav__inner">
            {sections.map((section) => {
              const path = sectionPath(section);
              return <Link aria-current={pathname === path ? 'page' : undefined} key={section.slug} to={path}>{section.label}</Link>;
            })}
          </div>
        </nav>
      </header>
      {searchOpen && (
        <div className="search-panel">
          <form className="shell search-panel__form" role="search" onSubmit={(event) => event.preventDefault()}>
            <Search size={20} aria-hidden="true" />
            <label className="sr-only" htmlFor="site-search">Search the Sentinel</label>
            <input id="site-search" autoFocus placeholder="Search stories, authors and topics" />
            <button className="icon-button" type="button" onClick={() => setSearchOpen(false)} aria-label="Close search"><X size={20} /></button>
          </form>
        </div>
      )}
      {menuOpen && (
        <div className="mobile-drawer" role="dialog" aria-modal="true" aria-label="Site navigation">
          <div className="mobile-drawer__top"><span>Sections</span><button className="icon-button" onClick={() => setMenuOpen(false)} aria-label="Close navigation"><X size={22} /></button></div>
          <nav>{sections.map((section) => {
            const path = sectionPath(section);
            return <Link aria-current={pathname === path ? 'page' : undefined} key={section.slug} onClick={() => setMenuOpen(false)} to={path}>{section.label}</Link>;
          })}</nav>
          <p>Independent metropolitan journalism for Chicago.</p>
        </div>
      )}
    </>
  );
};
